import React from "react";

import { Button, Input } from "@nextui-org/react";
import { X } from "lucide-react";

import IconLocation from "@/icons/icon_location.svg";
import { cn } from "@/utils/cn";

// Extracted input component for reuse
interface SmartAutocompleteInputProps {
  value: string;
  onChange: (val: string) => void;
  onClear: () => void;
  placeholder: string;
  isOpen: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  className?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const SmartAutocompleteInput: React.FC<SmartAutocompleteInputProps> = ({
  value,
  onChange,
  onClear,
  placeholder,
  isOpen,
  inputRef,
  autoFocus,
  className,
  onFocus,
  onBlur,
  onKeyPress,
}) => {
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;

    if (input.selectionStart === 0 && input.selectionEnd === 0) {
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }

    onFocus?.(e);
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={handleFocus}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.nativeEvent.stopImmediatePropagation();
        }
        onKeyPress?.(e);
      }}
      placeholder={placeholder}
      className={className ?? "h-full"}
      classNames={{
        inputWrapper: cn(
          "group-data-[focus-visible=true]:ring-0 group-data-[focus-visible=true]:ring-offset-0",
          className
            ? "!bg-white"
            : "h-full !bg-white shadow-none border-1 border-gray-100 md:border-none",
        ),
        innerWrapper: "grid grid-cols-[auto_1fr_36px]",
        input: "!pl-0 caret-[#323232] md:focus:placeholder-transparent",
      }}
      radius="lg"
      startContent={
        <IconLocation
          className={cn(
            "w-9 h-9 pointer-events-none mx-3",
            isOpen && "text-[#323232]",
          )}
        />
      }
      endContent={
        value.trim() ? (
          <Button
            isIconOnly
            size="lg"
            variant="light"
            onPress={onClear}
            className="text-default-400"
          >
            <X className="w-9 h-9 text-[#CF1409]" />
          </Button>
        ) : null
      }
      size={"lg"}
      autoFocus={autoFocus}
    />
  );
};

export default SmartAutocompleteInput;
